import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { SliderCartesImageNom, Team } from '@components/slider-cartes-image-nom/models';
import { ButtonMiddleOfficeComponent } from '@shared/components/button-middle-office';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { CapitalizeNamePipe, GenerateRangePipe } from '@shared/pipe';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { LogsService } from '@shared/services/logs';
import { MiddleOfficeService } from '@shared/services/middle-office';
import { ResponsiveService } from '@shared/services/responsive';
import { PageStore } from '@stores';
import { take } from 'rxjs';

const transformCycle = [
  'rotate-[-1.59deg] translate-x-[0.075px] translate-y-[-7.095px]',
  'rotate-[2deg] translate-x-[0.04px] translate-y-[1.395px]',
  'rotate-[2deg] translate-x-[4px] translate-y-[-4px]',
];

@Component({
  selector: 'app-slider-cartes-image-nom',
  imports: [
    TitleComponent,
    ImageComponent,
    NgClass,
    NgTemplateOutlet,
    GenerateRangePipe,
    ButtonMiddleOfficeComponent,
    CapitalizeNamePipe,
  ],
  templateUrl: './slider-cartes-image-nom.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderCartesImageNomComponent extends AbstractDynamicComponent<SliderCartesImageNom> {
  readonly isMiddleOfficeAvailable = inject(MiddleOfficeService).isMiddleOfficeAvailable;
  // The slides are only rendered once the team has been fetched, hence a non-required query.
  readonly slidesElementRef = viewChild<ElementRef<HTMLElement>>('slidesElementRef');
  readonly teams = signal<Team[]>([]);
  currentSlide = signal(0);
  nSlides = computed(() => Math.ceil(this.teams().length / this.kitchenersPerSlide()));
  private readonly device = inject(ResponsiveService).breakpoints.asReadonly();
  isMobile = computed(() => this.device() === 'mobile');
  kitchenersPerSlide = computed(() => (this.isMobile() ? 4 : 12));
  private scrollDebounce?: number;
  private isManualScroll = true;
  private readonly destroyRef = inject(DestroyRef);
  private readonly storeUuid = inject(PageStore).selectPath((state) => state.config?.context?.uuid);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => this.manageScrollListener());
    effect(() => this.fetchTeams());
  }

  getStickerTransform(index: number) {
    return transformCycle[index % transformCycle.length];
  }

  setCurrentSlide(index: number) {
    const slidesElement = this.slidesElementRef()?.nativeElement;

    if (!slidesElement) return;

    this.isManualScroll = false;
    this.currentSlide.set(index);

    const slideWidth = slidesElement.clientWidth;

    slidesElement.scrollTo({
      left: index * slideWidth,
      behavior: 'smooth',
    });

    // Wait for the animation to be done before listening to manual scrolling again.
    setTimeout(() => (this.isManualScroll = true), 200);
  }

  private fetchTeams() {
    const storeUuid = this.storeUuid();

    if (!storeUuid) return;

    this.elasticSearchService
      .getStoreTeams(storeUuid)
      .pipe(take(1))
      .subscribe({
        next: (teams) => this.teams.set(teams),
        error: (err: unknown) => this.logs.error('SliderCartesImageNomComponent', 'getStoreTeams failed', err),
      });
  }

  private manageScrollListener() {
    const slidesElementRef = this.slidesElementRef();

    if (!slidesElementRef) return;

    const slidesElement = slidesElementRef.nativeElement;

    const scrollListener = () => {
      if (this.scrollDebounce) clearTimeout(this.scrollDebounce);
      this.scrollDebounce = Number(setTimeout(() => this.updateCurrentSlideFromScroll(), 50));
    };

    slidesElement.addEventListener('scroll', scrollListener);
    this.destroyRef.onDestroy(() => slidesElement.removeEventListener('scroll', scrollListener));
  }

  private updateCurrentSlideFromScroll() {
    if (!this.isManualScroll) return;

    const slidesElement = this.slidesElementRef()?.nativeElement;

    if (!slidesElement) return;

    const scrollLeft = slidesElement.scrollLeft;
    const slideWidth = slidesElement.clientWidth;

    const currentSlideIndex = Math.round(scrollLeft / slideWidth);
    const clampedIndex = Math.max(0, Math.min(currentSlideIndex, this.nSlides() - 1));

    if (clampedIndex !== this.currentSlide()) this.currentSlide.set(clampedIndex);
  }
}
